// Descripción de los modelos LLM configurados (nombre comercial,
// características por familia). La usan la página "Modelos LLM" y el selector
// de modelo del chat. Qué modelos existen NO sale de acá: sale de "Modelos
// configurados" (GET /credits/models).

// Características por familia de modelo. Un modelo configurado que no encaje
// en ninguna se muestra igual, con su ID y una descripción genérica.
const FAMILY_TRAITS = {
  fable: {
    label: 'Fable',
    description: 'El modelo de mayor capacidad de la plataforma, para investigaciones largas y problemas de muchos pasos.',
    capabilities: [
      'Razonamiento de máxima profundidad',
      'Investigaciones de varios pasos',
      'Análisis de grandes volúmenes',
      'Síntesis ejecutiva'
    ],
    performance: 'Máximo',
    speed: 'Moderada',
    color: '#DB2777'
  },
  opus: {
    label: 'Opus',
    description: 'El modelo más capaz de la familia Claude, optimizado para tareas complejas de razonamiento y análisis de datos empresariales.',
    capabilities: [
      'Razonamiento avanzado',
      'Análisis de datos complejos',
      'Generación de consultas SQL sofisticadas',
      'Comprensión contextual profunda'
    ],
    performance: 'Excelente',
    speed: 'Moderada',
    color: '#7C3AED'
  },
  sonnet: {
    label: 'Sonnet',
    description: 'Equilibrio entre capacidad y velocidad, ideal para la mayoría de los análisis de inteligencia de negocios.',
    capabilities: [
      'Análisis de datos estructurados',
      'Generación de consultas SQL',
      'Interpretación de modelos semánticos',
      'Respuestas contextuales'
    ],
    performance: 'Muy bueno',
    speed: 'Rápida',
    color: '#2563EB'
  },
  haiku: {
    label: 'Haiku',
    description: 'El modelo más rápido de la familia Claude, ideal para consultas frecuentes y respuestas inmediatas.',
    capabilities: [
      'Consultas SQL estándar',
      'Respuestas rápidas',
      'Resúmenes y clasificación',
      'Alto volumen de consultas'
    ],
    performance: 'Bueno',
    speed: 'Muy rápida',
    color: '#059669'
  }
};

const GENERIC_TRAITS = {
  label: null,
  description: 'Modelo de lenguaje configurado en la plataforma y disponible para tus agentes.',
  capabilities: [],
  performance: 'No especificado',
  speed: 'No especificada',
  color: '#64748B'
};

// Ventana de contexto solo donde está confirmada (familia 3.x/4.x); para el
// resto la fila no se muestra en vez de inventar un número.
const CONTEXT_WINDOW_BY_MAJOR = { 3: '200K tokens', 4: '200K tokens' };

// IDs de Bedrock, con o sin prefijo de región. Nombres nuevos: familia antes de
// la versión, con o sin fecha ("us.anthropic.claude-opus-4-5-20251101-v1:0",
// "us.anthropic.claude-opus-5-5", "us.anthropic.claude-opus-4-6-v1"). Nombres
// viejos: versión antes de la familia ("anthropic.claude-3-7-sonnet-20250219-v1:0").
const NEW_NAMING = /claude-(opus|sonnet|haiku|fable)-(\d+)(?:-(\d)(?!\d))?(?:-(\d{8}))?(?:-(v[\w:.]+))?/;
const OLD_NAMING = /claude-(\d+)(?:-(\d)(?!\d))?-(opus|sonnet|haiku)(?:-(\d{8}))?(?:-(v[\w:.]+))?/;

export const describeModel = ({ provider, model }) => {
  const id = String(model || '');
  let family = null;
  let major = null;
  let minor = null;
  let date = null;
  let revision = null;

  const fresh = id.match(NEW_NAMING);
  const legacy = fresh ? null : id.match(OLD_NAMING);
  if (fresh) {
    [, family, major, minor, date, revision] = fresh;
  } else if (legacy) {
    [, major, minor, family, date, revision] = legacy;
  }

  const traits = (family && FAMILY_TRAITS[family]) || GENERIC_TRAITS;
  const isAnthropic = Boolean(family) || /anthropic/i.test(String(provider || ''));
  const versionLabel = major ? `${major}${minor ? `.${minor}` : ''}` : '';

  return {
    ...traits,
    id,
    family,
    versionNumber: major ? Number(`${major}.${minor || 0}`) : 0,
    name: family ? `Claude ${traits.label} ${versionLabel}`.trim() : id,
    provider: isAnthropic ? 'Anthropic' : (provider || 'Proveedor no especificado'),
    version: [date, revision].filter(Boolean).join('-'),
    contextWindow: CONTEXT_WINDOW_BY_MAJOR[major] || null,
    recommended: false
  };
};

// Versión mínima de Claude en la plataforma (2026-10-14): las anteriores a
// 4.5 están en desuso. Espejo de MIN_CLAUDE_VERSION en
// whisperbi-credits-engine/src/credits-engine/pricing_resolver.py, que es el
// que realmente rechaza darlas de alta.
export const MIN_CLAUDE_VERSION = 4.5;

export const isDeprecatedClaudeModel = (modelId) => {
  const { family, versionNumber } = describeModel({ model: modelId });
  return Boolean(family) && versionNumber < MIN_CLAUDE_VERSION;
};

// Una sola tarjeta "RECOMENDADO": el Opus más reciente configurado (mismo
// criterio que antes -- el más capaz para análisis empresarial -- sin
// marcar a todos los Opus cuando hay varias versiones).
export const markRecommended = (models) => {
  const opus = models.filter((m) => m.family === 'opus');
  if (opus.length === 0) return models;
  const best = opus.reduce((a, b) => (b.versionNumber > a.versionNumber ? b : a));
  return models.map((m) => (m === best ? { ...m, recommended: true } : m));
};
